import React from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UsersSettingsTab, UsersSettingsTabs } from "./tabs";
import { UserRowModel } from "./SettingsUsersTable";
import { useInviteFoodicsAccounts } from "@/hooks/useInviteFoodicsAccounts";
import { customToast } from "@/utils/customToast";
import * as Sentry from "@sentry/react";
import { useAuth } from "@/Auth";
import { EditRoleDialog } from "./EditRoleDialog";
import { useT } from "@/i18n";

type UserDetailsDialogProps = {
  show: boolean;
  user: UserRowModel;
  selectedTab: UsersSettingsTab;
  businessId?: string;
  onCancel: () => void;
};

type DialogView = "details" | "editRole";

export function UserDetailsDialog({
  show,
  user,
  selectedTab,
  businessId,
  onCancel,
}: UserDetailsDialogProps) {
  const auth = useAuth();
  const userIsDemo = auth.user?.is_demo ?? false;
  const inviteFoodicsAccounts = useInviteFoodicsAccounts();
  const [currentView, setCurrentView] = React.useState<DialogView>("details");
  const { t, tn } = useT();

  const userStatus =
    selectedTab === UsersSettingsTabs.active ? t("users.biAccount") : t("users.foodicsAccount");

  const handleInvite = async () => {
    try {
      if (!businessId) {
        Sentry.captureEvent({
          message: "selected business is not expected to be undefined in invite's handler",
          level: "error",
        });
        return;
      }

      await inviteFoodicsAccounts.mutateAsync({
        businessId,
        accountIds: [user.id],
        userIsDemo,
      });

      onCancel();
    } catch (error) {
      console.error("Failed to handle invitation:", error);
      customToast(t("users.inviteFailed"), "error", "bottom");
    }
  };

  const handleEditRole = () => {
    setCurrentView("editRole");
  };

  const handleRoleDialogClose = () => {
    setCurrentView("details");
  };

  const handleCloseAll = () => {
    setCurrentView("details");
    onCancel();
  };

  const isButtonLoading = inviteFoodicsAccounts.isPending;

  if (!show) {
    return null;
  }

  if (currentView === "editRole") {
    return (
      <EditRoleDialog
        show={true}
        onCancel={handleRoleDialogClose}
        user={user}
        onRoleChangeSuccess={handleCloseAll}
      />
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && handleCloseAll()}>
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        <DialogHeader className="pe-12">
          <DialogTitle dir="auto" className="truncate leading-normal">
            {user.name || user.email || "—"}
          </DialogTitle>
        </DialogHeader>
        <dl className="flex flex-col divide-y px-4 py-1">
          <DetailRow label={t("common.name")}>
            <span dir="auto">{user.name || "—"}</span>
          </DetailRow>
          <DetailRow
            label={t("users.status")}
            action={
              !auth.user?.is_demo &&
              selectedTab === UsersSettingsTabs.inactive && (
                <Button size="sm" onClick={handleInvite} disabled={isButtonLoading}>
                  {isButtonLoading ? t("common.loadingEllipsis") : t("users.invite")}
                </Button>
              )
            }
          >
            {userStatus}
          </DetailRow>
          <DetailRow label={t("common.email")}>
            <span dir="auto">{user.email || "—"}</span>
          </DetailRow>
          <DetailRow label={t("users.branches")}>
            {tn("users.branchCount", user.branchesCount ?? 0)}
          </DetailRow>
          {selectedTab === UsersSettingsTabs.active && (
            <DetailRow
              label={t("users.role")}
              action={
                !user.is_root &&
                !auth.user?.is_demo && (
                  <Button variant="outline" size="sm" onClick={handleEditRole}>
                    {t("common.edit")}
                  </Button>
                )
              }
            >
              {user.is_root ? t("users.admin") : t("users.normal")}
            </DetailRow>
          )}
        </dl>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({
  label,
  action,
  children,
}: React.PropsWithChildren<{ label: string; action?: React.ReactNode }>) {
  return (
    <div className="flex min-h-13 items-center gap-3 py-2">
      <dt className="w-20 shrink-0 font-label text-muted-foreground">{label}</dt>
      <dd className="flex min-w-0 flex-1 items-center justify-between gap-2">
        <span className="min-w-0 break-words">{children}</span>
        {action}
      </dd>
    </div>
  );
}
